import * as React from 'react';

import { IJsonQuery } from 'shared/common/packages/api-models/common/json_query';

import { IExperimentalFeatures } from '../../types/filters';
import { TModifier } from '../../types/modifier';
import { ISpecialPromo } from '../../types/specialPromo';
import { IContext, context as Context } from '../../utils/context';
import { modifyJsonQuery } from '../../utils/modifyJsonQuery';

export interface IOnChangeParameters {
  appliedModifiers: [TModifier, ...TModifier[]];
  nextJsonQuery: IJsonQuery;
}

interface IProviderProps {
  jsonQuery: IJsonQuery;
  specialPromo?: ISpecialPromo;
  features: IExperimentalFeatures;
  onChange(parameters: IOnChangeParameters): void;
  onApply(): void;
  onClear?(): void;
  onApplyClick?(): void;
  onAdvancedFiltersOpen?(): void;
}

export const Provider: React.FC<IProviderProps> = ({
  children,
  jsonQuery,
  specialPromo,
  onChange,
  onApply,
  onClear,
  onApplyClick,
  features,
  onAdvancedFiltersOpen,
}) => {
  const handleChange = React.useCallback(
    (...modifiers: [TModifier, ...TModifier[]]) => {
      onChange({
        appliedModifiers: modifiers,
        nextJsonQuery: modifyJsonQuery(jsonQuery, modifiers),
      });
    },
    [jsonQuery, onChange],
  );

  const context = React.useMemo<IContext>(
    () => ({
      jsonQuery,
      specialPromo,
      onChange: handleChange,
      onApply,
      onClear,
      onApplyClick,
      onAdvancedFiltersOpen,
      features,
    }),
    [handleChange, jsonQuery, onAdvancedFiltersOpen, onApply, onApplyClick, onClear, specialPromo, features],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
